/**
 * SignInGate — email/password sign-in + account-creation forms backed by BetterAuth.
 *
 * Shown by /app when there is no valid session. Two modes:
 *   "login"  — returning user sign-in form
 *   "signup" — new-user registration (invite + Turnstile required)
 *
 * R4.x visual register (Wave 1 migration):
 *   - Tokens from packages/ui/src/tokens/tokens.css (--bg, --surface, --text,
 *     --muted, --faint, --line, --accent, --radius-card, --shadow-cta)
 *   - Form primitives from @proof/ui/forms (Form, TextField, FormError)
 *   - Inter font via --font-sans; Geist Mono reserved for data display only
 *   - Card: max-width 400px, --radius-card (12px), 0.5px border --line, --surface bg
 *   - Primary CTA: --accent bg, warm-white text, --shadow-cta micro-shadow
 *   - No shadows except the permitted CTA shadow (design.md §4.6, §2 principle 2)
 *
 * Architecture refs:
 *   architecture.md §4.11 — Better Auth 1.6.8 on Workers + D1
 *   architecture.md §4.25 — Turnstile client-side widget
 *   .plan/implementation/2026-04-26-phase-4-handover.md §4.1
 *
 * Voice rules (branding.md §5.2.1 + §5.5):
 *   No banned words. No exclamations. No `we`/`us`/`our`. Precise and quiet.
 *
 * Design rules (design.md §2, §4):
 *   Hairline borders. Warm neutrals. Inter for shell prose.
 *   Accent on primary CTA + focus rings only. CTA micro-shadow only.
 *   200ms ease on state changes.
 *
 * Turnstile:
 *   The real TurnstileWidget is mounted on the signup form. On dev, the
 *   build wires Cloudflare's published always-passes test site key so the
 *   challenge passes invisibly; the api Worker is paired with the matching
 *   test secret.
 *
 * Backend wiring preserved:
 *   - POST /auth/login (not /api/auth/sign-in/email) for rate-limit + Turnstile wrapper
 *   - POST /auth/signup for invite-gated wrapper
 *   - data-turnstile-state on signup form — global-setup.ts waits on this attribute
 *   - All data-testid attributes preserved for existing e2e selectors
 *
 * CLAUDE.md Rule 7 — verified at 375px, 768px, 1280px.
 * CLAUDE.md Rule 8 — every error path surfaces via inline FormError (RHF
 *   root.serverError pattern) and console.error. No bare catch {}.
 */

import { useState, useCallback, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";
import { TurnstileWidget, type TurnstileWidgetRef } from "./TurnstileWidget";
import { Form, TextField, FormError } from "@proof/ui/forms";
import { readPendingGroupJoin, clearPendingGroupJoin } from "~/features/groups/pendingGroupJoin";
// R4 WP-3 — share→signup attribution (a KIND, never a URL).
import { readShareLanding, clearShareLanding } from "~/features/share/shareLandingStamp";
import {
  readPendingFriendInvite,
  clearPendingFriendInvite,
} from "~/features/friends/pendingFriendInvite";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SignInGateProps {
  /**
   * Called after the server confirms a valid session. The parent page will
   * re-check `/api/auth/get-session` and mount Workspace.
   */
  onSessionEstablished: () => void;
  /**
   * Which form to show on initial render. Defaults to "login".
   * The /sign-up route passes "signup" so users land in the correct mode
   * without having to click the mode-switch affordance.
   */
  initialMode?: "login" | "signup";
}

// ── RHF field schemas ─────────────────────────────────────────────────────────

interface LoginFields {
  email: string;
  password: string;
}

interface SignupFields {
  name: string;
  handle: string;
  email: string;
  password: string;
  invite: string;
}

interface ResetRequestFields {
  email: string;
}

// Rule 8: cap every auth request (login, signup, password-reset-request) so a
// stalled fetch can never latch its pending CTA ("Signing in…" / "Creating
// account…" / "Sending…") forever. On timeout the fetch is aborted and the
// per-flow catch surfaces a retryable failure.
const AUTH_REQUEST_TIMEOUT_MS = 15_000;

// ── CSS ───────────────────────────────────────────────────────────────────────

const CSS = `
  /* ── Page shell ── */
  .psg {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100dvh;
    padding: var(--space-8, 32px) var(--space-4, 16px);
    font-family: var(--font-sans);
    background: var(--bg, #f7f5f1);
  }

  /* ── Card ── design.md §4.4: --radius-card 12px, hairline 0.5px --line ── */
  .psg__card {
    width: 100%;
    max-width: 400px;
    padding: var(--card-padding-mobile, 20px);
    background: var(--surface);
    border: 0.5px solid var(--line, #dcd7cc);
    border-radius: var(--radius-card, 12px);
  }

  @media (min-width: 768px) {
    .psg__card {
      padding: var(--card-padding-desktop, 24px);
    }
  }

  /* ── Wordmark ── branding.md §4.1: Inter 600, -0.02em tracking ── */
  .psg__wordmark {
    font-family: var(--font-sans);
    font-size: 18px;
    font-weight: 600;
    color: var(--text, #1a1714);
    letter-spacing: -0.02em;
    margin: 0 0 var(--space-5, 20px);
    display: block;
    user-select: none;
  }

  .psg__wordmark-period {
    color: var(--accent, #c23d12);
  }

  /* ── Form title ── */
  .psg__title {
    margin: 0 0 var(--space-5, 20px);
    font-family: var(--font-sans);
    font-size: var(--text-base, 15px);
    font-weight: 500;
    color: var(--text, #1a1714);
    letter-spacing: -0.01em;
    line-height: var(--lh-base, 1.5);
  }

  /* ── Field wrapper — applied as className on each TextField ── */
  .psg__field {
    display: flex;
    flex-direction: column;
    gap: var(--space-1, 4px);
    margin-bottom: var(--space-3, 12px);
  }

  /* Label inside TextField ([data-label] is set by @radix-ui/react-label) */
  .psg__field [data-label] {
    font-family: var(--font-sans);
    font-size: 11px;
    font-weight: 500;
    color: var(--muted, #71685b);
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  /* Input inside TextField */
  .psg__field input {
    width: 100%;
    box-sizing: border-box;
    height: 34px;
    min-height: var(--tap-target-min, 44px);
    padding: 0 var(--space-3, 12px);
    font-family: var(--font-sans);
    font-size: var(--text-sm, 13px);
    color: var(--text, #1a1714);
    background: var(--bg, #f7f5f1);
    border: 1px solid var(--line, #dcd7cc);
    border-radius: 6px;
    outline: none;
    transition: border-color var(--motion-state, 200ms ease);
    -webkit-appearance: none;
  }

  .psg__field input:focus {
    border-color: var(--accent, #c23d12);
    /* box-shadow removed — design.md §2 principle 2: the only permitted shadow is
       the CTA micro-shadow. Focus ring is handled by the global :focus-visible
       rule in packages/ui/src/tokens/tokens.css (Ember 2px outline + 2px offset). */
  }

  .psg__field input[aria-invalid="true"] {
    border-color: #b94a48;
  }

  /* Inline field-level error from TextField */
  .psg__field [data-field-error] {
    font-family: var(--font-sans);
    font-size: 12px;
    color: #b94a48;
    margin-top: var(--space-1, 4px);
  }

  /* Hint text below a field */
  .psg__hint {
    font-family: var(--font-sans);
    font-size: 11px;
    color: var(--faint, #766d60);
    margin-top: var(--space-1, 4px);
  }

  /* ── Form-level error (server / network / rate-limit errors) ── */
  .psg__form-error {
    margin-top: var(--space-3, 12px);
    padding: var(--space-3, 12px);
    font-family: var(--font-sans);
    font-size: var(--text-sm, 13px);
    color: var(--text, #1a1714);
    background: var(--bg, #f7f5f1);
    border: 0.5px solid var(--line, #dcd7cc);
    border-radius: 6px;
    line-height: var(--lh-base, 1.5);
    margin-bottom: 0;
  }

  /* ── Turnstile container ── */
  .psg__turnstile {
    margin-top: var(--space-3, 12px);
    display: flex;
    justify-content: flex-start;
  }

  /* ── Primary CTA ── design.md §4.6: only the CTA carries --shadow-cta ── */
  .psg__cta {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 36px;
    min-height: var(--tap-target-min, 44px);
    margin-top: var(--space-4, 16px);
    font-family: var(--font-sans);
    font-size: var(--text-sm, 13px);
    font-weight: 500;
    /* Warm white per design.md §2 — never pure #ffffff */
    color: #f7f5f1;
    background: var(--accent, #c23d12);
    border: none;
    border-radius: var(--radius-button, 8px);
    cursor: pointer;
    /* design.md §4.6: only permitted shadow */
    box-shadow: var(--shadow-cta, 0 1px 2px rgba(0, 0, 0, 0.04));
    transition: opacity var(--motion-state, 200ms ease);
    -webkit-tap-highlight-color: transparent;
  }

  .psg__cta:hover:not(:disabled) {
    opacity: 0.88;
  }

  .psg__cta:disabled {
    /* Explicit disabled colors — opacity-collapse dropped white-on-accent to
       ~1.34:1 (illegible). --cta-disabled-fg on --cta-disabled-bg reads 4.0:1
       (legible) yet below enabled 5.29:1 so it still reads disabled. */
    background: var(--cta-disabled-bg);
    color: var(--cta-disabled-fg);
    box-shadow: none;
    cursor: not-allowed;
  }

  /* ── Inline "Forgot password" affordance below the password field.
     Right-aligned secondary link, never the accent colour (Ember reserved
     for primary CTAs per design.md §2 principle 1). */
  .psg__forgot {
    display: flex;
    justify-content: flex-end;
    margin-top: calc(-1 * var(--space-2, 8px));
    margin-bottom: var(--space-3, 12px);
  }

  .psg__forgot a {
    background: none;
    border: none;
    padding: 0;
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--muted, #71685b);
    cursor: pointer;
    text-decoration: none;
    transition: color var(--motion-state, 200ms ease);
    -webkit-tap-highlight-color: transparent;
  }

  .psg__forgot a:hover,
  .psg__forgot a:focus-visible {
    color: var(--text, #1a1714);
    text-decoration: underline;
  }

  /* ── Quiet status notice — used for the post-reset flash on /sign-in and
     for the constant-time confirmation on the reset-request form. Same
     visual treatment as .psg__form-error but without the error colour. */
  .psg__notice {
    margin-bottom: var(--space-4, 16px);
    padding: var(--space-3, 12px);
    font-family: var(--font-sans);
    font-size: var(--text-sm, 13px);
    color: var(--text, #1a1714);
    background: var(--bg, #f7f5f1);
    border: 0.5px solid var(--line, #dcd7cc);
    border-radius: 6px;
    line-height: var(--lh-base, 1.5);
  }

  /* ── Lede text on the reset-request form ── */
  .psg__lede {
    margin: 0 0 var(--space-4, 16px);
    font-family: var(--font-sans);
    font-size: var(--text-sm, 13px);
    color: var(--muted, #71685b);
    line-height: var(--lh-base, 1.5);
  }

  /* ── Mode toggle (sign-in ↔ create account) ── */
  .psg__switch {
    margin-top: var(--space-4, 16px);
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--muted, #71685b);
    text-align: center;
    line-height: var(--lh-base, 1.5);
  }

  .psg__switch-btn {
    background: none;
    border: none;
    padding: 0;
    font-family: var(--font-sans);
    font-size: 12px;
    /* design.md §2 principle 1: Ember (--accent) is reserved for primary CTAs
       and signal elements only — not secondary affordances. Use --text instead. */
    color: var(--text, #1a1714);
    cursor: pointer;
    text-decoration: none;
    transition: text-decoration var(--motion-state, 200ms ease);
    -webkit-tap-highlight-color: transparent;
  }

  .psg__switch-btn:hover,
  .psg__switch-btn:focus-visible {
    text-decoration: underline;
  }

  /* ── Beta invite-only notice ────────────────────────────────────────────────
     Shown ABOVE the signup form when there is no URL invite token — tells the
     visitor proof. is invite-only and provides a route to the waitlist.
     design.md §2: no accent on this element (not a primary CTA).
     branding.md §5.2.1: no exclamations, no banned phrases. */
  .psg__beta-notice {
    margin: 0 0 var(--space-4, 16px);
    padding: var(--space-3, 12px);
    font-family: var(--font-sans);
    font-size: var(--text-sm, 13px);
    color: var(--text, #1a1714);
    background: var(--bg, #f7f5f1);
    border: 0.5px solid var(--line, #dcd7cc);
    border-radius: 6px;
    line-height: var(--lh-base, 1.5);
  }

  .psg__beta-notice a {
    color: var(--text, #1a1714);
    text-decoration: underline;
    transition: opacity var(--motion-state, 200ms ease);
  }

  .psg__beta-notice a:hover {
    opacity: 0.7;
  }

  .psg__beta-notice a:focus-visible {
    outline: 2px solid var(--accent, #c23d12);
    outline-offset: 2px;
    border-radius: 2px;
  }

  /* ── Access footer ──────────────────────────────────────────────────────────
     Quiet footer line below the sign-in form — signposts that proof. is
     invite-only and links to the marketing waitlist.
     design.md §2: no accent on this element. branding.md §5.2.1 voice rules. */
  .psg__access-footer {
    margin-top: var(--space-4, 16px);
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--faint, #766d60);
    text-align: center;
    line-height: var(--lh-base, 1.5);
  }

  .psg__access-footer a {
    color: var(--muted, #71685b);
    text-decoration: underline;
    transition: color var(--motion-state, 200ms ease);
  }

  .psg__access-footer a:hover {
    color: var(--text, #1a1714);
  }

  .psg__access-footer a:focus-visible {
    outline: 2px solid var(--accent, #c23d12);
    outline-offset: 2px;
    border-radius: 2px;
  }

  /* ── Terms/privacy agreement line (signup) ──────────────────────────────────
     Quiet single line directly below the Create-account CTA. Not a checkbox —
     account creation is itself the act of agreement, and a checkbox would need
     a backend field to be honest. Same muted register as .psg__access-footer.
     design.md §2: no accent (not a primary CTA). branding.md §5.2.1: no
     exclamations, no banned words. */
  .psg__agree {
    margin: var(--space-3, 12px) 0 0;
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--faint, #766d60);
    text-align: center;
    line-height: var(--lh-base, 1.5);
  }

  .psg__agree a {
    color: var(--muted, #71685b);
    text-decoration: underline;
    transition: color var(--motion-state, 200ms ease);
  }

  .psg__agree a:hover {
    color: var(--text, #1a1714);
  }

  .psg__agree a:focus-visible {
    outline: 2px solid var(--accent, #c23d12);
    outline-offset: 2px;
    border-radius: 2px;
  }

  /* ── Email-verification status notice ───────────────────────────────────────
     Shown above the sign-in form when the URL carries ?verified=1 (redirected
     from the BA verify-email endpoint). No accent colour, no icon, no
     exclamation — branding.md §5.2.1. Same visual treatment as .psg__notice. */
  .psg__verify-notice {
    margin-bottom: var(--space-4, 16px);
    padding: var(--space-3, 12px);
    font-family: var(--font-sans);
    font-size: var(--text-sm, 13px);
    color: var(--text, #1a1714);
    background: var(--bg, #f7f5f1);
    border: 0.5px solid var(--line, #dcd7cc);
    border-radius: 6px;
    line-height: var(--lh-base, 1.5);
  }
`;

// Inject CSS once — idempotent. Using a versioned ID so Wave 0's old
// proof-sign-gate-css is replaced cleanly without a stale-style fight.
if (typeof document !== "undefined") {
  const STYLE_ID = "psg-r4x-css";
  if (!document.getElementById(STYLE_ID)) {
    // Remove any legacy style injected by the pre-R4.x implementation.
    const legacyEl = document.getElementById("proof-sign-gate-css");
    if (legacyEl) legacyEl.remove();

    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Read the stored invite token from the URL query param (?invite=...). */
function readInviteToken(): string {
  if (typeof location !== "undefined") {
    const params = new URLSearchParams(location.search);
    return params.get("invite") ?? "";
  }
  return "";
}

/**
 * Read the pre-filled email from the URL query param (?email=...).
 * buildInviteUrl() embeds the invited address so the signup form can pre-fill
 * it (token + email both pre-filled = minimal friction). Kept editable.
 */
function readEmailParam(): string {
  if (typeof location !== "undefined") {
    const params = new URLSearchParams(location.search);
    return params.get("email") ?? "";
  }
  return "";
}

/**
 * Derive the form mode. An invite token in the URL (?invite=...) always forces
 * "signup" regardless of route/initialMode, so an invited user who lands on the
 * login-mode gate (e.g. the legacy /app?invite= links already in inboxes) still
 * sees the create-account form instead of a password prompt they never set.
 * Reset-password `view` state only applies when mode === "login", so forcing
 * signup here is safe — it never strands a reset flow.
 */
export function deriveMode(
  initialMode: "login" | "signup",
  inviteToken: string,
): "login" | "signup" {
  return inviteToken ? "signup" : initialMode;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function SignInGate({ onSessionEstablished, initialMode = "login" }: SignInGateProps) {
  // Mode is driven by the route (login for /sign-in, signup for /sign-up),
  // EXCEPT when an invite token is present in the URL — then we force "signup"
  // so invited users land on the create-account form even on a login-mode gate
  // (rescues existing /app?invite= links already in inboxes). See deriveMode().
  // <Link> navigation replaces in-component toggles so the URL always reflects
  // the form state — back/forward works correctly.
  const inviteToken = readInviteToken();
  const mode = deriveMode(initialMode, inviteToken);

  // ── Group-invite admission (operator Q1, R1 wave 2) ──────────────────────────
  // A signed-out visitor who followed a group invite lands here via
  // routes/join.tsx, which persisted the invite token in the pending-group-join
  // store. When present, the group token is the beta-admission credential — the
  // separate waitlist invite-token field is not shown or required, and the token
  // rides the signup POST as `groupInviteToken` (the api half accepts it in the
  // same style as the waitlist `invite`). Read once on mount (the store is
  // written before navigation, so it is present on the first render).
  //
  // State, not a mount-time const (R1 wave 2 fix): a dead-token 403 clears the
  // pending-group-join store, and the component must then EXIT group-admission
  // mode so the waitlist invite field becomes reachable again — a mount-time
  // const would leave the component locked in group-admission mode, re-POSTing
  // the dead token on every resubmit with no way to enter a waitlist invite.
  const [groupInviteToken, setGroupInviteToken] = useState<string | null>(() =>
    readPendingGroupJoin(),
  );
  const groupAdmission = groupInviteToken !== null;

  // ── Friend-invite admission (beta access, 2026-07-12) ────────────────────────
  // A signed-out visitor who followed a personal friend-invite link lands here via
  // routes/friend-invite.tsx, which persisted the token in the pending-friend-
  // invite store. The friend invite is a THIRD beta-admission credential
  // (globally capped): it rides the signup POST as `friendInvite`, the separate
  // waitlist invite-token field is not shown, and the server either admits (with
  // beta + auto-friend) or, past the global cap, DEFLECTS to the guest list
  // (`waitlisted:true`, no account). Group admission takes precedence when both
  // are somehow pending — mirrors resolvePendingFriendInviteRedirect's ordering.
  //
  // State, not a mount-time const (same rationale as groupInviteToken): a dead
  // token must be able to clear itself and exit friend-admission mode.
  const [friendInviteToken, setFriendInviteToken] = useState<string | null>(() =>
    readPendingFriendInvite(),
  );
  const friendAdmission = !groupAdmission && friendInviteToken !== null;

  // ── Signup-awaiting-verification confirmation (R1 wave 2 fix) ─────────────────
  // On prod REQUIRE_EMAIL_VERIFICATION is ON, so /auth/signup returns 200 WITHOUT
  // a session cookie (`sessionEstablished: false`). Calling onSessionEstablished()
  // unconditionally on res.ok dead-ends the flow — the SPA thinks it is signed in
  // while no session exists. When the api reports the session was withheld we show
  // a quiet confirmation instead and leave the user to verify their email.
  const [signupAwaitingVerification, setSignupAwaitingVerification] = useState(false);

  // ── Guest-list deflection confirmation (friend-invite beta cap, 2026-07-12) ──
  // The global beta-admission cap is full: /auth/signup returned 200 with
  // `waitlisted:true` — NO account, NO session, a place held on the guest list.
  // Distinct from signupAwaitingVerification (account created, session pending
  // email verification): here nothing was created. Renders a calm guest-list
  // confirmation instead of the signup form.
  const [signupWaitlisted, setSignupWaitlisted] = useState(false);

  // ── Inline view swap within the "login" route ───────────────────────────────
  // Per the password-reset feature plan §4.3, "Forgot password" swaps the
  // current /sign-in surface into a single-field email + Turnstile form
  // rather than navigating to a new route. The plan's open-question Q3
  // resolves to inline — one fewer route to register, cleaner mobile flow.
  // `view` is local UI state and only applies when `mode === "login"`.
  const [view, setView] = useState<"login" | "reset-request">("login");

  // ── /sign-in?reset=ok flash ────────────────────────────────────────────────
  // Set by /reset-password on successful password update. Surfaces a quiet
  // helper line above the sign-in form so the user knows the reset went
  // through. The flag is read once on mount — no need to clear it from the
  // URL since the page reload on next navigation drops the query anyway.
  const [searchParams] = useSearchParams();
  const showResetFlash = useMemo(() => searchParams.get("reset") === "ok", [searchParams]);

  // ── /sign-in?verified=1 notice ────────────────────────────────────────────
  // Set by Better Auth's verify-email callbackURL redirect after the user
  // clicks their confirmation email link. On success: verified=1, no error.
  // On failure (expired/invalid token): verified=1&error=<code>.
  const showVerifiedNotice = useMemo(
    () => searchParams.get("verified") === "1" && !searchParams.get("error"),
    [searchParams],
  );
  const showVerifiedError = useMemo(
    () => searchParams.get("verified") === "1" && !!searchParams.get("error"),
    [searchParams],
  );

  // Preserve `returnTo` across the sign-in ↔ sign-up mode-switch <Link>s. Without
  // this the invite flow loses the destination: a visitor on
  // /sign-in?returnTo=/app/join/<token> who taps "Create one" would land on a
  // bare /sign-up and, after signing up, be dropped at /app instead of the join
  // page. The dedicated /sign-up + /sign-in routes read this same param.
  const modeSwitchQuery = useMemo(() => {
    const returnTo = searchParams.get("returnTo");
    return returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
  }, [searchParams]);

  // ── Constant-time confirmation for reset-request ───────────────────────────
  // The server returns the same body whether the email exists or not
  // (architecture.md §4.11 BA wrapper). The UI mirrors that — once submit
  // succeeds we render a single confirmation line and hide the form.
  const [resetRequestSent, setResetRequestSent] = useState(false);

  // ── Turnstile state (signup + reset-request) ───────────────────────────────
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetRef>(null);
  // The reset-request form needs its own Turnstile widget instance so its
  // single-use token doesn't collide with the signup widget. Keeping the
  // existing `turnstileToken`/`turnstileRef` for signup and adding a parallel
  // pair for the reset-request flow.
  const [resetTurnstileToken, setResetTurnstileToken] = useState<string | null>(null);
  const resetTurnstileRef = useRef<TurnstileWidgetRef>(null);

  // ── RHF — Login form ────────────────────────────────────────────────────────
  const loginForm = useForm<LoginFields>({
    defaultValues: { email: "", password: "" },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  // ── RHF — Signup form ───────────────────────────────────────────────────────
  const signupForm = useForm<SignupFields>({
    defaultValues: {
      name: "",
      handle: "",
      // Pre-fill from ?email= (buildInviteUrl embeds it). Editable.
      email: readEmailParam(),
      password: "",
      invite: inviteToken,
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  // ── RHF — Reset-request form ────────────────────────────────────────────────
  // Single email field. Per open-question Q2 the success flash does NOT
  // auto-fill the sign-in email — the user is left with the same minimal flow.
  const resetRequestForm = useForm<ResetRequestFields>({
    defaultValues: { email: "" },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    // Token expired or the widget errored — clear local state so submit is
    // gated until the user re-passes. Per Rule 8, surface failure paths.
    setTurnstileToken(null);
  }, []);

  // Parallel handlers for the reset-request Turnstile widget. They have to be
  // separate functions, not shared with signup's handlers, because the two
  // widgets each hold their own one-shot token.
  const handleResetTurnstileVerify = useCallback((token: string) => {
    setResetTurnstileToken(token);
  }, []);

  const handleResetTurnstileError = useCallback(() => {
    setResetTurnstileToken(null);
  }, []);

  // Reset errors when switching mode. Called from the <Link> onClick so state
  // clears before the new route renders.
  const handleModeSwitch = useCallback(() => {
    loginForm.clearErrors();
    signupForm.clearErrors();
  }, [loginForm, signupForm]);

  // Inline view swap within /sign-in — used by "Forgot password" and the
  // "Back to sign-in" link on the reset-request form. Clearing errors on
  // each side avoids stale messages bleeding across views.
  const handleViewSwitch = useCallback(
    (next: "login" | "reset-request") => {
      loginForm.clearErrors();
      resetRequestForm.clearErrors();
      // Drop any stale Turnstile token from the other view so a stale state
      // never accidentally satisfies the gate.
      setResetTurnstileToken(null);
      setResetRequestSent(false);
      setView(next);
    },
    [loginForm, resetRequestForm],
  );

  // ── Sign-in submit ──────────────────────────────────────────────────────────

  const handleLogin = useCallback(
    async (data: LoginFields) => {
      loginForm.clearErrors();

      // Rule 8: cap the request so a stalled /auth/login can never latch
      // "Signing in…" forever. On timeout the fetch is aborted and the catch
      // surfaces a retryable failure.
      const controller = new AbortController();
      const timeout = setTimeout(() => {
        console.error(`[SignInGate] login timed out after ${AUTH_REQUEST_TIMEOUT_MS}ms; aborting`);
        controller.abort();
      }, AUTH_REQUEST_TIMEOUT_MS);

      try {
        // POST to the api Worker's invite-gated wrapper at /auth/login
        // (rate-limited + Turnstile-aware after 3 failures), NOT BetterAuth's
        // raw /api/auth/sign-in/email handler. The api Worker mounts
        // BetterAuth at /api/auth/* and the wrappers at /auth/*.
        const res = await fetch("/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          signal: controller.signal,
          body: JSON.stringify({ email: data.email.trim(), password: data.password }),
        });

        if (res.ok) {
          onSessionEstablished();
          return;
        }

        const json = (await res.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
        };

        if (res.status === 429) {
          loginForm.setError("root.serverError", {
            message: "Too many sign-in attempts. Wait a moment and try again.",
          });
          return;
        }
        if (res.status === 401 || json.error === "INVALID_CREDENTIALS") {
          loginForm.setError("root.serverError", { message: "Incorrect email or password." });
          return;
        }
        loginForm.setError("root.serverError", {
          message: json.message ?? "Sign-in did not complete. Try again.",
        });
      } catch (err) {
        // Rule 8: surface the failure (inline form error) AND log the
        // underlying error (including an abort from the timeout above) so an
        // upstream boundary can pick it up.
        console.error("[SignInGate] login network error", err);
        loginForm.setError("root.serverError", {
          message: "Could not reach the server. Check your connection and try again.",
        });
      } finally {
        clearTimeout(timeout);
      }
    },
    [loginForm, onSessionEstablished],
  );

  // ── Sign-up submit ──────────────────────────────────────────────────────────

  const handleSignup = useCallback(
    async (data: SignupFields) => {
      signupForm.clearErrors();

      // Block submission until the Turnstile widget produces a token. On dev,
      // the test site key auto-passes near-instantly; on prod, the user solves
      // the real challenge. Either way, we never POST without a token.
      if (!turnstileToken) {
        signupForm.setError("root.serverError", {
          message: "Verification has not finished yet. Wait a moment and try again.",
        });
        return;
      }
      const tsToken = turnstileToken;

      // R4 WP-3 — read the share-landing stamp BEFORE the request so the funnel
      // attribution rides the same POST that creates the account. Null for every
      // visit that did not start on a public share surface.
      const shareLanding = readShareLanding();

      // Rule 8: cap the request so a stalled /auth/signup can never latch
      // "Creating account…" forever with no error. On timeout we abort the
      // fetch, which throws below and routes through the catch (surface + reset).
      const controller = new AbortController();
      const timeout = setTimeout(() => {
        console.error(`[SignInGate] signup timed out after ${AUTH_REQUEST_TIMEOUT_MS}ms; aborting`);
        controller.abort();
      }, AUTH_REQUEST_TIMEOUT_MS);

      try {
        // POST to the api Worker's invite-gated wrapper at /auth/signup,
        // NOT BetterAuth's raw /api/auth/sign-up/email handler. The wrapper
        // enforces the invite token, Turnstile, rate limits, and
        // handle-claiming on top of BA's create-user.
        const res = await fetch("/auth/signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          signal: controller.signal,
          body: JSON.stringify({
            email: data.email.trim(),
            password: data.password,
            name: data.name.trim(),
            handle: data.handle.trim().toLowerCase(),
            turnstileToken: tsToken,
            // Waitlist invite token. OMITTED entirely in group- OR friend-
            // admission mode: the server schema declares each credential as
            // `z.string().min(1).optional()` (apps/api/src/routes/auth/index.ts),
            // so an empty string fails min(1) and 400s the whole request —
            // absence is the correct encoding for "no waitlist token". A group
            // or friend token is the alternative admission credential. Server
            // precedence is invite > groupInvite > friendInvite; the client sends
            // exactly one, matching that order (group first, then friend).
            ...(groupInviteToken
              ? { groupInviteToken }
              : friendAdmission && friendInviteToken
                ? { friendInvite: friendInviteToken }
                : { invite: data.invite.trim() }),
            // R4 WP-3 — share→signup attribution. The KIND of public surface
            // this tab's visit started on ("share_recipe" / "share_profile"),
            // stamped on mount by the share + profile views. Never a URL, never
            // a recipe uuid / slug / handle: the server enum is the contract and
            // it re-validates. Omitted entirely when the visit did not start on
            // a share surface, so nothing changes for a direct signup.
            ...(shareLanding ? { landing: shareLanding } : {}),
          }),
        });

        if (res.ok) {
          // Parse the body to learn whether BA issued a session cookie. On prod
          // (REQUIRE_EMAIL_VERIFICATION ON) the session is withheld until the
          // user verifies their email, so `sessionEstablished` is false — we must
          // NOT treat the account as signed in. Backward-compat: an older api that
          // omits the field leaves it undefined, and we preserve the previous
          // behaviour (dev sets the cookie immediately → proceed as signed in).
          const okJson = (await res.json().catch(() => ({}))) as {
            sessionEstablished?: boolean;
            waitlisted?: boolean;
          };
          if (okJson.waitlisted === true) {
            // Friend-invite beta cap full: no account, no session — a place was
            // held on the guest list instead. Clear the pending friend invite so
            // it doesn't re-trigger a signup attempt on a later sign-in in this
            // browser, and render the calm guest-list confirmation.
            clearPendingFriendInvite();
            setFriendInviteToken(null);
            setSignupWaitlisted(true);
            return;
          }
          // R4 WP-3 — an account now exists (this is below the `waitlisted`
          // branch, where none was created), so the server has already recorded
          // the attribution. Drop the stamp: it describes this visit's signup and
          // must not be replayed onto a second account created in the same tab.
          clearShareLanding();
          if (okJson.sessionEstablished === false) {
            // Account created; session pending email verification. The pending
            // group-join token stays in the store so it survives the round trip
            // and auto-files once the session finally lands after sign-in.
            setSignupAwaitingVerification(true);
            return;
          }
          onSessionEstablished();
          return;
        }

        const json = (await res.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
        };

        // The Turnstile token is single-use; on any non-OK response, reset the
        // widget so the next submit cycle gets a fresh challenge token.
        turnstileRef.current?.reset();
        setTurnstileToken(null);

        if (res.status === 429) {
          // The group-invite signup path enforces a per-IP hourly cap that
          // returns 429 {error:"RATE_LIMITED"}. It is NOT an invalid-invite
          // signal: keep the pending token (do not clear it) and show transient
          // copy so the user knows to wait rather than believing the link is dead.
          if (json.error === "RATE_LIMITED") {
            signupForm.setError("root.serverError", {
              message: "Too many attempts from this network. Wait an hour, then try again.",
            });
            return;
          }
          signupForm.setError("root.serverError", {
            message: "Too many sign-up attempts. Wait a moment and try again.",
          });
          return;
        }
        if (json.error === "HANDLE_TAKEN") {
          signupForm.setError("handle", { message: "That handle is not available." });
          return;
        }
        if (json.error === "ACCESS_DENIED" || json.error === "GROUP_INVITE_INVALID") {
          if (groupAdmission) {
            // The group token is dead (invalid / expired / revoked / lifetime cap
            // exhausted). Drop it so it doesn't haunt a later sign-in on this
            // browser, AND exit group-admission mode (clear the token state) so
            // the waitlist invite field becomes reachable — otherwise the
            // component stays locked re-POSTing the dead token with no recourse.
            clearPendingGroupJoin();
            setGroupInviteToken(null);
            signupForm.setError("root.serverError", {
              message: "This group invite is no longer valid. Ask the group owner for a new link.",
            });
            return;
          }
          if (friendAdmission) {
            // The friend-invite token is dead (invalid / expired / revoked — the
            // server flattens these to one generic ACCESS_DENIED). Drop it so it
            // doesn't haunt a later sign-in, and exit friend-admission mode so the
            // waitlist invite field becomes reachable.
            clearPendingFriendInvite();
            setFriendInviteToken(null);
            signupForm.setError("root.serverError", {
              message:
                "This invite link is no longer valid. Ask the person who invited you for a new one.",
            });
            return;
          }
          signupForm.setError("invite", { message: "Invite token not valid." });
          return;
        }
        if (json.error === "TURNSTILE_FAILED") {
          signupForm.setError("root.serverError", {
            message: "Verification did not pass. Try again.",
          });
          return;
        }
        signupForm.setError("root.serverError", {
          message: json.message ?? "Account creation did not complete. Try again.",
        });
      } catch (err) {
        // Rule 8: a thrown network error (including an abort from the timeout
        // above) must not silently dismiss. Reset the single-use Turnstile
        // token so the user can retry, then surface the error.
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        console.error("[SignInGate] signup network error", err);
        signupForm.setError("root.serverError", {
          message: "Could not reach the server. Check your connection and try again.",
        });
      } finally {
        clearTimeout(timeout);
      }
    },
    [
      signupForm,
      turnstileToken,
      onSessionEstablished,
      groupInviteToken,
      groupAdmission,
      friendInviteToken,
      friendAdmission,
    ],
  );

  // ── Reset-request submit ────────────────────────────────────────────────────

  const handleResetRequest = useCallback(
    async (data: ResetRequestFields) => {
      resetRequestForm.clearErrors();

      // Match the signup form: never POST without a Turnstile token. On dev
      // the test site key auto-passes; on prod the user solves the real one.
      if (!resetTurnstileToken) {
        resetRequestForm.setError("root.serverError", {
          message: "Verification has not finished yet. Wait a moment and try again.",
        });
        return;
      }
      const tsToken = resetTurnstileToken;

      // Rule 8: cap the request so a stalled reset-request can never latch
      // "Sending…" forever. On timeout the fetch is aborted and the catch
      // surfaces a retryable failure.
      const controller = new AbortController();
      const timeout = setTimeout(() => {
        console.error(
          `[SignInGate] reset-request timed out after ${AUTH_REQUEST_TIMEOUT_MS}ms; aborting`,
        );
        controller.abort();
      }, AUTH_REQUEST_TIMEOUT_MS);

      try {
        const res = await fetch("/auth/password-reset/request", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          signal: controller.signal,
          body: JSON.stringify({
            email: data.email.trim(),
            turnstileToken: tsToken,
          }),
        });

        if (res.ok) {
          // Constant-time response: render the confirmation regardless of
          // whether the email exists. The wrapper at /auth/password-reset/request
          // forwards to BA which returns the same body either way.
          setResetRequestSent(true);
          return;
        }

        // Turnstile token is single-use — reset the widget on failure so the
        // user can retry with a fresh challenge.
        resetTurnstileRef.current?.reset();
        setResetTurnstileToken(null);

        const json = (await res.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
        };

        if (res.status === 429) {
          resetRequestForm.setError("root.serverError", {
            message: "Too many reset requests. Wait a moment and try again.",
          });
          return;
        }
        if (json.error === "TURNSTILE_FAILED") {
          resetRequestForm.setError("root.serverError", {
            message: "Verification did not pass. Try again.",
          });
          return;
        }
        resetRequestForm.setError("root.serverError", {
          message: json.message ?? "Reset request did not complete. Try again.",
        });
      } catch (err) {
        // Rule 8: surface failure + log; reset the single-use token. Covers an
        // abort from the timeout above as well as any network error.
        resetTurnstileRef.current?.reset();
        setResetTurnstileToken(null);
        console.error("[SignInGate] reset-request network error", err);
        resetRequestForm.setError("root.serverError", {
          message: "Could not reach the server. Check your connection and try again.",
        });
      } finally {
        clearTimeout(timeout);
      }
    },
    [resetRequestForm, resetTurnstileToken],
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  const isLoginSubmitting = loginForm.formState.isSubmitting;
  const isSignupSubmitting = signupForm.formState.isSubmitting;
  const isResetRequestSubmitting = resetRequestForm.formState.isSubmitting;

  return (
    <div className="psg" data-testid="sign-gate">
      <div className="psg__card">
        {/* Wordmark — branding.md §4.1: Inter weight 600, accent period */}
        <span className="psg__wordmark" aria-label="proof.">
          proof<span className="psg__wordmark-period">.</span>
        </span>

        <h1 className="psg__title">
          {mode === "signup"
            ? "Create account"
            : view === "reset-request"
              ? "Reset password"
              : "Sign in"}
        </h1>

        {/* Post-reset flash. Surfaces only on the login view of /sign-in
            when the URL carries ?reset=ok (set by /reset-password on
            successful confirm). Quiet, period-terminated, no exclamation —
            branding.md §5.2.1. */}
        {mode === "login" && view === "login" && showResetFlash && (
          <p className="psg__notice" data-testid="reset-flash" role="status">
            Password updated. Sign in.
          </p>
        )}

        {/* Email-verification notice. Surfaces on the login view when BA
            redirects back with ?verified=1 after the user clicks their
            confirmation link. No accent colour, no exclamation — branding.md §5.2.1.
            On BA failure the callbackURL gets ?error=... appended — show the
            error variant instead. */}
        {mode === "login" && view === "login" && showVerifiedNotice && (
          <p className="psg__verify-notice" data-testid="verify-notice" role="status">
            Email confirmed. Sign in to continue.
          </p>
        )}
        {mode === "login" && view === "login" && showVerifiedError && (
          <p className="psg__verify-notice" data-testid="verify-notice-error" role="status">
            That confirmation link is no longer valid.
          </p>
        )}

        {/* ── Login form ── */}
        {mode === "login" && view === "login" && (
          <Form form={loginForm} onSubmit={handleLogin}>
            <TextField<LoginFields>
              name="email"
              label="Email"
              type="email"
              autoComplete="email"
              autoFocus
              disabled={isLoginSubmitting}
              className="psg__field"
              registerOptions={{
                required: "Email is required.",
                validate: (v) => v.trim().length > 0 || "Email is required.",
              }}
              data-testid="gate-email"
            />

            <TextField<LoginFields>
              name="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              disabled={isLoginSubmitting}
              className="psg__field"
              registerOptions={{ required: "Password is required." }}
              data-testid="gate-password"
            />

            {/* "Forgot password" — secondary affordance below the password
                field. Inline mode-swap per plan §4.3 / open-question Q3.
                Right-aligned muted link, never the accent colour
                (design.md §2 principle 1 — Ember reserved for primary CTAs).
                Rule 9 discoverability: visible on every viewport at this
                breakpoint — the parent card is the same width across all. */}
            <div className="psg__forgot">
              <a
                href="#forgot-password"
                onClick={(e) => {
                  e.preventDefault();
                  handleViewSwitch("reset-request");
                }}
                data-testid="gate-forgot-password"
              >
                Forgot password
              </a>
            </div>

            {/* Server-level / network errors — RHF root.serverError convention.
                Rule 8: no silent swallow; all error paths route here. */}
            <FormError className="psg__form-error" />

            <button
              type="submit"
              className="psg__cta"
              disabled={isLoginSubmitting}
              data-testid="gate-submit"
            >
              {isLoginSubmitting ? "Signing in…" : "Sign in"}
            </button>
          </Form>
        )}

        {/* ── Reset-request form (inline mode swap on /sign-in) ────────────── */}
        {mode === "login" && view === "reset-request" && !resetRequestSent && (
          <Form
            form={resetRequestForm}
            onSubmit={handleResetRequest}
            dataAttrs={{
              "data-turnstile-state": resetTurnstileToken ? "verified" : "pending",
            }}
          >
            <p className="psg__lede">
              Enter the email on your account. A reset link will be sent if a matching account
              exists.
            </p>

            <TextField<ResetRequestFields>
              name="email"
              label="Email"
              type="email"
              autoComplete="email"
              autoFocus
              disabled={isResetRequestSubmitting}
              className="psg__field"
              registerOptions={{
                required: "Email is required.",
                validate: (v) => v.trim().length > 0 || "Email is required.",
              }}
              data-testid="reset-request-email"
            />

            <div className="psg__turnstile" data-testid="reset-request-turnstile">
              <TurnstileWidget
                ref={resetTurnstileRef}
                onVerify={handleResetTurnstileVerify}
                onError={handleResetTurnstileError}
              />
            </div>

            {/* Rule 8: every error path surfaces here. */}
            <FormError className="psg__form-error" />

            <button
              type="submit"
              className="psg__cta"
              disabled={isResetRequestSubmitting}
              data-testid="reset-request-submit"
            >
              {isResetRequestSubmitting ? "Sending…" : "Send reset link"}
            </button>
          </Form>
        )}

        {/* Reset-request constant-time confirmation. The server returns the
            same body whether the email exists or not — the UI mirrors that. */}
        {mode === "login" && view === "reset-request" && resetRequestSent && (
          <p className="psg__notice" data-testid="reset-request-sent" role="status">
            If this email exists, a reset link has been sent.
          </p>
        )}

        {/* ── Beta invite-only notice (signup without URL invite) ────────────
             Shown ABOVE the form when mode is "signup" but no invite token
             was pre-filled from the URL. Tells the visitor that proof. is
             invite-only and provides a route to the waitlist.
             Hidden when an invite token is present in the URL — those users
             already have access and don't need the signpost. (design.md §2,
             branding.md §5.2.1 — no exclamations, no banned phrases.) */}
        {mode === "signup" &&
          !signupAwaitingVerification &&
          !signupWaitlisted &&
          !inviteToken &&
          !groupAdmission &&
          !friendAdmission && (
            <p className="psg__beta-notice" data-testid="beta-notice">
              proof. is invite-only during beta. <a href="/#request-access">Request access.</a>
            </p>
          )}

        {/* ── Group-admission notice ──────────────────────────────────────────
             Shown ABOVE the signup form when the visitor arrived via a group
             invite (operator Q1). The group invite is the beta-admission
             credential, so no separate waitlist token is needed — this line
             explains the absence of the invite-token field. (design.md §2 —
             no accent; branding.md §5.2.1 — no exclamations, no banned words.) */}
        {mode === "signup" && !signupAwaitingVerification && groupAdmission && (
          <p className="psg__beta-notice" data-testid="group-admission-notice">
            You’re creating an account to join a group. No separate invite token is needed.
          </p>
        )}

        {/* ── Friend-invite admission notice ──────────────────────────────────
             Shown ABOVE the signup form when the visitor arrived via a personal
             friend invite (beta access, 2026-07-12). The friend invite is the
             beta-admission credential, so no separate waitlist token is needed —
             this line explains the absence of the invite-token field. (design.md
             §2 — no accent; branding.md §5.2.1 — no exclamations, no banned
             words.) */}
        {mode === "signup" &&
          !signupAwaitingVerification &&
          !signupWaitlisted &&
          friendAdmission && (
            <p className="psg__beta-notice" data-testid="friend-admission-notice">
              You’re joining proof. with an invite. No separate invite token is needed.
            </p>
          )}

        {/* ── Signup-awaiting-verification confirmation ──────────────────────────
             Prod path: /auth/signup returned 200 with sessionEstablished:false —
             the account exists but the session is held until the email is
             verified. Quiet confirmation, consistent with the ?verified=1 notice
             copy above. No accent, no exclamation — branding.md §5.2.1. */}
        {mode === "signup" && signupAwaitingVerification && (
          <p className="psg__verify-notice" data-testid="signup-verify-pending" role="status">
            Account created. Check your email for a link to confirm your address, then sign in.
          </p>
        )}

        {/* ── Guest-list deflection confirmation ─────────────────────────────────
             Friend-invite beta cap full: /auth/signup returned waitlisted:true —
             no account was created, a place is held on the guest list. Quiet,
             period-terminated, no exclamation. Copy aligns with the guest-list
             EMAIL (plan §3.4 ①). branding.md §5.2.1 — no banned words. */}
        {mode === "signup" && signupWaitlisted && (
          <p className="psg__verify-notice" data-testid="signup-waitlisted" role="status">
            You’re on the guest list. Beta is full right now — when a seat opens, a setup link comes
            to your email.
          </p>
        )}

        {/* ── Signup form ── */}
        {mode === "signup" && !signupAwaitingVerification && !signupWaitlisted && (
          <Form
            form={signupForm}
            onSubmit={handleSignup}
            dataAttrs={{ "data-turnstile-state": turnstileToken ? "verified" : "pending" }}
          >
            <TextField<SignupFields>
              name="name"
              label="Name"
              type="text"
              autoComplete="name"
              autoFocus
              disabled={isSignupSubmitting}
              className="psg__field"
              registerOptions={{ required: "Name is required." }}
              data-testid="gate-name"
            />

            <TextField<SignupFields>
              name="handle"
              label="Handle"
              type="text"
              autoComplete="username"
              disabled={isSignupSubmitting}
              className="psg__field"
              registerOptions={{
                required: "Handle is required.",
                pattern: {
                  value: /^[a-z0-9_-]+$/i,
                  message: "Handle may only contain letters, numbers, hyphens, and underscores.",
                },
              }}
              data-testid="gate-handle"
            />

            <TextField<SignupFields>
              name="email"
              label="Email"
              type="email"
              autoComplete="email"
              disabled={isSignupSubmitting}
              className="psg__field"
              registerOptions={{
                required: "Email is required.",
                validate: (v) => v.trim().length > 0 || "Email is required.",
              }}
              data-testid="gate-email"
            />

            <TextField<SignupFields>
              name="password"
              label="Password"
              type="password"
              autoComplete="new-password"
              disabled={isSignupSubmitting}
              className="psg__field"
              registerOptions={{
                required: "Password must be at least 12 characters.",
                minLength: {
                  value: 12,
                  message: "Password must be at least 12 characters.",
                },
              }}
              data-testid="gate-password"
            />

            {/* Invite token — hint + Turnstile widget follow below the field.
                aria-describedby wires the hint to the input for screen readers.
                Hidden in group- OR friend-admission mode: the invite link is the
                admission credential, so the field is neither shown nor
                registered — no required-validation runs and the group/friend
                token rides the POST as `groupInviteToken`/`friendInvite`. */}
            {!groupAdmission && !friendAdmission && (
              <>
                <TextField<SignupFields>
                  name="invite"
                  label="Invite token"
                  type="text"
                  autoComplete="off"
                  disabled={isSignupSubmitting}
                  className="psg__field"
                  registerOptions={{ required: "An invite token is required." }}
                  data-testid="gate-invite"
                  aria-describedby="psg-invite-hint"
                />
                <span className="psg__hint" id="psg-invite-hint">
                  Invites arrive by email when access opens.
                </span>
              </>
            )}
            <div className="psg__turnstile" data-testid="gate-turnstile">
              <TurnstileWidget
                ref={turnstileRef}
                onVerify={handleTurnstileVerify}
                onError={handleTurnstileError}
              />
            </div>

            {/* Server-level / network errors — RHF root.serverError convention.
                Rule 8: no silent swallow; all error paths route here. */}
            <FormError className="psg__form-error" />

            <button
              type="submit"
              className="psg__cta"
              disabled={isSignupSubmitting}
              data-testid="gate-submit"
            >
              {isSignupSubmitting ? "Creating account…" : "Create account"}
            </button>

            {/* Agreement line — the published terms and privacy policy live on
                the marketing site, not in the SPA routing table, so plain <a>
                rather than react-router <Link>. Opened in a new tab so a
                part-filled signup form is never discarded. /legal/* is excluded
                from Service Worker navigation ownership
                (features/pwa/sw-nav-match.ts), so the copy is always fresh. */}
            <p className="psg__agree" data-testid="signup-agree">
              By creating an account you agree to the{" "}
              <a href="/legal/terms" target="_blank" rel="noopener">
                Terms of service
              </a>{" "}
              and{" "}
              <a href="/legal/privacy" target="_blank" rel="noopener">
                Privacy policy
              </a>
              .
            </p>
          </Form>
        )}

        {/* Mode toggle — design.md §2: labelled, accessible link to the other mode.
            Rule 9: discoverability — sign-up is reachable from sign-in at every
            viewport via a visible labelled button. */}
        <div className="psg__switch">
          {mode === "login" && view === "login" && (
            <>
              No account?{" "}
              <Link
                to={`/sign-up${modeSwitchQuery}`}
                className="psg__switch-btn psg__mode-toggle-link"
                onClick={handleModeSwitch}
              >
                Create one
              </Link>
            </>
          )}
          {mode === "login" && view === "reset-request" && (
            <button
              type="button"
              className="psg__switch-btn"
              onClick={() => handleViewSwitch("login")}
              data-testid="reset-request-back"
            >
              Back to sign-in
            </button>
          )}
          {mode === "signup" && (
            <>
              Already have an account?{" "}
              <Link
                to={`/sign-in${modeSwitchQuery}`}
                className="psg__switch-btn psg__mode-toggle-link"
                onClick={handleModeSwitch}
              >
                Sign in
              </Link>
            </>
          )}
        </div>

        {/* ── Invite-only footer (sign-in mode) ───────────────────────────────
             Quiet signpost below the sign-in form. Plain <a> (not react-router
             Link) because /#request-access is on the marketing site, not in
             the SPA routing table. (design.md §2, branding.md §5.2.1 — no
             exclamations, no banned phrases, period at end.) */}
        {mode === "login" && view === "login" && (
          <p className="psg__access-footer" data-testid="access-footer">
            proof. is invite-only during beta. <a href="/#request-access">Request access.</a>
          </p>
        )}
      </div>
    </div>
  );
}
